import React, { useState, useEffect } from 'react'

import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Divider from '@material-ui/core/Divider'
import { styles } from './versionCard.styles'
import { useTranslation } from 'react-i18next'

import { PriceDetails, PriceVersion } from '../../../pricing.Model'
import moment from 'moment'
import PriceExpressionValidator from '../../../../../components/shared/priceExpressionValidator/priceExpressionValidator'
import { EnumCommodity, EnumPrecioUnitario } from '../../../../../api'
import { EnumPriceCalculationType, EnumRedondeo } from '../../../../../api/Argentia/enums'
import { enumKeyToValue, enumValueToKey } from '../../../../../helpers'
import { useConfirmationDialog } from '../../../../../hooks'
import { ConfirmationDialogs } from '../../../../../model/enums/confirmationDialogs'
import { checkGapOverlapVersions, checkGapOverlapVersionsDelete } from '../../utils/datesChecker'
import { EnumPriceType } from '../../../../../api/oData/enums/enumPriceType'

interface VersionCardProps {
  priceVersion: PriceVersion
  price: PriceDetails
  expanded: boolean
  defaultExpanded: boolean
  canDelete: boolean
  unMountOnExit: boolean
  versions: any[]
  firstVersionStartDate?: Date
  isValid?: boolean
  onHandleChange: (versionNumber: number | null) => void
  onDeleteVersion: () => void
  onEditVersion: (version) => void
  onIsValidated: (versionId: number, isValid: boolean) => void
  onRefreshPage?: () => void
}

interface VersionCardModel {
  id: number
  expression: string | undefined
  precioUnitario: EnumPrecioUnitario
  priceCalculationType: EnumPriceCalculationType
  redondeo: EnumRedondeo
  startDate: string
  endDate?: string | null
}

const VersionCard: React.FC<VersionCardProps> = ({
  price,
  priceVersion,
  defaultExpanded,
  onHandleChange,
  canDelete,
  onDeleteVersion,
  onEditVersion,
  firstVersionStartDate,
  onIsValidated,
  unMountOnExit = false,
  versions,
  isValid = undefined
}) => {
  const [t] = useTranslation(['versions', 'pricingDetails', 'globalerisLibrary', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [isExpanded, setIsExpanded] = useState<boolean | undefined>(defaultExpanded)
  const [expressionValidated, setExpressionValidated] = useState<boolean>(false)
  const [state, setState] = useState<VersionCardModel>({
    id: priceVersion.id,
    expression: priceVersion.expression,
    precioUnitario: enumValueToKey(EnumPrecioUnitario, priceVersion.precioUnitario),
    priceCalculationType: enumValueToKey(
      EnumPriceCalculationType,
      priceVersion.priceCalculationType
    ),
    redondeo: enumValueToKey(EnumRedondeo, priceVersion.redondeo),
    startDate: priceVersion.startDate,
    endDate: priceVersion.endDate
  })
  const [tempState, setTempState] = useState<Partial<VersionCardModel> | undefined | null>(
    undefined
  )
  const [priceUnitSelected, setPriceUnitSelected] = useState<EnumPrecioUnitario>(
    enumValueToKey(EnumPrecioUnitario, priceVersion.precioUnitario)
  )
  const [rounding, setRounding] = useState<EnumRedondeo>(
    enumValueToKey(EnumRedondeo, priceVersion.redondeo)
  )
  const [startDate, setStartDate] = useState<string>(priceVersion.startDate)
  const [endDate, setEndDate] = useState<string | undefined | null>(priceVersion.endDate)
  const [isFormFilled, setIsFormFilled] = useState<boolean>(false)

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const handleChange = () => {
    onHandleChange(isExpanded ? null : priceVersion.version)
    setIsExpanded(status => !status)
  }

  const handleDeleteButton = () => {
    const [isGap, isOverlap] = checkGapOverlapVersionsDelete(state, versions)
    if (isGap[0] && isOverlap[0]) {
      getConfirmation(ConfirmationDialogs.GAP_OVERLAP).then(async () => {
        onDeleteVersion()
        closeConfirmation()
      })
    } else if (isGap[0]) {
      const errorMsg = isGap[1]
      if (errorMsg === 'BOTH') {
        getConfirmation(ConfirmationDialogs.GAP_BOTH).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      } else if (errorMsg === 'PAST') {
        getConfirmation(ConfirmationDialogs.GAP_PAST).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      } else if (errorMsg === 'FUTURE') {
        getConfirmation(ConfirmationDialogs.GAP_FUTURE).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      }
    } else if (isOverlap[0]) {
      const errorMsg = isOverlap[1]
      if (errorMsg === 'BOTH') {
        getConfirmation(ConfirmationDialogs.OVERLAP_BOTH).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      } else if (errorMsg === 'PAST') {
        getConfirmation(ConfirmationDialogs.OVERLAP_PAST).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      } else if (errorMsg === 'FUTURE') {
        getConfirmation(ConfirmationDialogs.OVERLAP_FUTURE).then(async () => {
          onDeleteVersion()
          closeConfirmation()
        })
      }
    } else {
      onDeleteVersion()
    }
  }

  const handleValidateButton = () => {
    const updatedState = { ...state, ...tempState }
    // In case user haven't made any changes on dates, no need to do this validation.
    if (tempState?.startDate || tempState?.endDate) {
      const [isGap, isOverlap] = checkGapOverlapVersions(updatedState, versions)
      if (isGap[0] && isOverlap[0]) {
        getConfirmation(ConfirmationDialogs.GAP_OVERLAP).then(async () => {
          setTempState(null)
          setState(updatedState)
          onEditVersion(tempState)
          closeConfirmation()
        })
      } else if (isGap[0]) {
        const errorMsg = isGap[1]
        if (errorMsg === 'BOTH') {
          getConfirmation(ConfirmationDialogs.GAP_BOTH).then(async () => {
            setTempState(null)
            setState(updatedState)
            onEditVersion(tempState)
            closeConfirmation()
          })
        } else if (errorMsg === 'PAST') {
          getConfirmation(ConfirmationDialogs.GAP_PAST).then(async () => {
            setTempState(null)
            setState(updatedState)
            onEditVersion(tempState)
            closeConfirmation()
          })
        } else if (errorMsg === 'FUTURE') {
          getConfirmation(ConfirmationDialogs.GAP_FUTURE).then(async () => {
            setTempState(null)
            setState(updatedState)
            onEditVersion(tempState)
            closeConfirmation()
          })
        }
      } else if (isOverlap[0]) {
        const errorMsg = isOverlap[1]
        if (errorMsg === 'BOTH') {
          getConfirmation(ConfirmationDialogs.OVERLAP_BOTH).then(async () => {
            setTempState(null)
            setState(updatedState)
            onEditVersion(tempState)
            closeConfirmation()
          })
        } else if (errorMsg === 'PAST') {
          getConfirmation(ConfirmationDialogs.OVERLAP_PAST).then(async () => {
            setTempState(null)
            setState(updatedState)

            onEditVersion(tempState)

            closeConfirmation()
          })
        } else if (errorMsg === 'FUTURE') {
          getConfirmation(ConfirmationDialogs.OVERLAP_FUTURE).then(async () => {
            setTempState(null)
            setState(updatedState)
            onEditVersion(tempState)
            closeConfirmation()
          })
        }
      } else {
        setTempState(null)
        setState(updatedState)
        onEditVersion(tempState)
      }
    } else {
      setTempState(null)
      setState(updatedState)
      onEditVersion(tempState)
    }
  }

  const handlePropChange = (prop, propName) => {
    if (propName === 'priceExpression' && prop !== undefined) {
      if (state.expression !== prop) {
        setTempState(tempState => ({ ...tempState, expression: prop }))
      }
    } else if (propName === 'priceCalculationType') {
      if (state.priceCalculationType !== prop) {
        setTempState(tempState => ({ ...tempState, priceCalculationType: prop }))
      }
    } else if (propName === 'priceUnit') {
      if (priceUnitSelected !== prop) {
        setTempState(tempState => ({ ...tempState, precioUnitario: prop }))
      }
    } else if (propName === 'rounding') {
      if (rounding !== prop) {
        setTempState(tempState => ({ ...tempState, redondeo: prop }))
      }
    } else if (propName === 'startDate') {
      setTempState(tempState => ({ ...tempState, startDate: prop }))
    } else if (propName === 'endDate') {
      setTempState(tempState => ({ ...tempState, endDate: prop }))
    }
  }

  useEffect(() => {
    if (tempState === null) {
      onIsValidated(priceVersion.id, true)
    } else if (tempState !== undefined) {
      onIsValidated(priceVersion.id, false)
    }
  }, [tempState])

  useEffect(() => {
    setStartDate(priceVersion.startDate)
    setEndDate(priceVersion.endDate)
    setState(state => ({
      ...state,
      startDate: priceVersion.startDate,
      endDate: priceVersion.endDate
    }))
    if (tempState !== null && tempState !== undefined && !isValid) {
      setTempState(tempState => ({
        ...tempState,
        startDate: priceVersion.startDate,
        endDate: priceVersion.endDate
      }))
    }
  }, [priceVersion])

  return (
    <>
      <Accordion
        elevation={3}
        TransitionProps={{ unmountOnExit: unMountOnExit }}
        // TransitionProps={{ mountOnEnter: true }}
        expanded={isExpanded}
        onChange={handleChange}
        // classes={{ root: { 'minWidth': '0' } }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          className={classes.accordionSummary}
          aria-controls="panel1c-content-${}"
          id={`panel1c-header-${price.code}${priceVersion.version}`}
          classes={{ content: classes.customAccordionSummaryContent }}
          IconButtonProps={{ edge: 'start' }}
        >
          <div className={classes.accordionHeader}>
            <Typography variant="h2" color="primary" noWrap>
              {t('versions:versionCard.summary.header', {
                version: `${priceVersion.version}${tempState && isFormFilled ? '*' : ''}`
              })}
            </Typography>
          </div>
          <div className={classes.accordionCenterColumn}>
            <Typography
              variant="h6"
              style={{
                // span styles
                minWidth: 0,
                width: '100%',
                fontSize: '14px',
                textOverflow: 'ellipsis',
                wordWrap: 'break-word'
                // display: 'inline-block'
              }}
              noWrap
            >
              {state.expression}
            </Typography>
          </div>
          <div className={classes.accordionLastColumn}>
            <Typography variant="caption" noWrap>
              {moment(startDate).format('DD/MM/YYYY')} -{' '}
              {endDate ? moment(endDate).format('DD/MM/YYYY') : null}
            </Typography>
          </div>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <div className={classes.accordionMain}>
            <PriceExpressionValidator
              showPriceUnit
              showRounding
              showDates
              show={price && priceVersion ? true : false}
              elevation={0}
              priceType={enumKeyToValue(EnumPriceType, price.priceType)}
              commodity={enumValueToKey(EnumCommodity, price.commodity)}
              activeExpression={state.expression}
              activeExpressionIsValid={true}
              activePriceCalculationType={state.priceCalculationType}
              activePriceUnit={state.precioUnitario}
              activeRounding={state.redondeo}
              activeVersionStartDate={moment(startDate).toDate().toString()}
              activeVersionEndDate={endDate ? moment(endDate).toDate().toString() : null}
              onFormValid={setIsFormFilled}
              onIsValid={isValid => {
                setExpressionValidated(isValid)
              }}
              onSelectValidExpression={expression =>
                handlePropChange(expression, 'priceExpression')
              }
              onSelectPriceCalculationType={selected =>
                handlePropChange(selected, 'priceCalculationType')
              }
              onSelectPriceUnit={selected => handlePropChange(selected, 'priceUnit')}
              onSelectRounding={selected => handlePropChange(selected, 'rounding')}
              onSelectStartDate={selected => handlePropChange(selected, 'startDate')}
              onSelectEndDate={selected => handlePropChange(selected, 'endDate')}
              priceVersion={priceVersion.version}
              price={price}
              active={isExpanded}
              minVersionStartDate={firstVersionStartDate ? firstVersionStartDate : undefined}
              disableStartDate={priceVersion.version === 1 ? true : false}
            />
          </div>
        </AccordionDetails>
        <Divider />
        <AccordionActions>
          <Button
            size="small"
            color="primary"
            onClick={handleValidateButton}
            disabled={tempState && isFormFilled ? false : true}
          >
            {t('versions:versionCard.actions.validate')}
          </Button>
          <Button
            size="small"
            color="primary"
            onClick={handleDeleteButton}
            disabled={priceVersion.version === 1 ? true : !canDelete}
          >
            {t('versions:versionCard.actions.delete')}
          </Button>
        </AccordionActions>
      </Accordion>
    </>
  )
}

export default React.memo(VersionCard)
